import { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from 'core/userContext';
import {
  DATE_FORMAT_WITH_COMMA_HOUR_MINS,
  displayDate,
  displayDateRange,
  displayDateTz,
  displayDateWithDayWeekMonthYearPrefix,
  displayDateWithTodayAndYesterdayPrefix,
} from '../../date';
import { localizeDateRange } from '../../l10n';
import { LOCALIZED_DATE_TIME_FEATURE_FLAG } from './constants';
import { displayDateWithLocalization } from './utils';

export const useLocalizedDateTimeFeatureFlag = () =>
  useSelector(selectFeatureFlags)[LOCALIZED_DATE_TIME_FEATURE_FLAG];

export type DisplayDateFn = (inputDate: Date | Moment, format?: string) => string;

export const useDisplayDate = (): DisplayDateFn => {
  const isLocalizedDateTimeFeatureFlagEnabled = useLocalizedDateTimeFeatureFlag();

  return (inputDate, format) =>
    isLocalizedDateTimeFeatureFlagEnabled && inputDate instanceof Date
      ? displayDateWithLocalization(inputDate, format)
      : displayDate(inputDate, format);
};

export type DisplayDateTzFn = (
  inputDate: Date | Moment,
  format?: string,
  timeZone?: string,
) => string;

export const useDisplayDateTz = (): DisplayDateTzFn => {
  const isLocalizedDateTimeFeatureFlagEnabled = useLocalizedDateTimeFeatureFlag();

  return (inputDate, format, timeZone) =>
    isLocalizedDateTimeFeatureFlagEnabled && inputDate instanceof Date
      ? displayDateWithLocalization(inputDate, format, { timeZone })
      : displayDateTz(inputDate, format, timeZone);
};

export type DisplayDateWithTodayAndYesterdayPrefixFn = (inputDate: Date | Moment) => string;
export const useDisplayDateWithTodayAndYesterdayPrefix =
  (): DisplayDateWithTodayAndYesterdayPrefixFn => {
    const isLocalizedDateTimeFeatureFlagEnabled = useLocalizedDateTimeFeatureFlag();

    return (inputDate) =>
      isLocalizedDateTimeFeatureFlagEnabled && inputDate instanceof Date
        ? displayDateWithLocalization(inputDate, DATE_FORMAT_WITH_COMMA_HOUR_MINS, {
            relative: true,
          })
        : displayDateWithTodayAndYesterdayPrefix(inputDate);
  };

export type DisplayDateWithDayWeekMonthYearPrefixFn = (
  inputDate: Date,
  withTime?: boolean,
) => string;

export const useDisplayDateWithDayWeekMonthYearPrefix =
  (): DisplayDateWithDayWeekMonthYearPrefixFn => {
    const isLocalizedDateTimeFeatureFlagEnabled = useLocalizedDateTimeFeatureFlag();

    return (inputDate, showTime = true) =>
      isLocalizedDateTimeFeatureFlagEnabled
        ? displayDateWithLocalization(inputDate, undefined, { relative: true, showTime })
        : displayDateWithDayWeekMonthYearPrefix(inputDate, showTime);
  };

export type DisplayDateRangeFn = (
  start: Date | Moment,
  end: Date | Moment,
  showTime?: boolean,
  twentyFourHour?: boolean,
  hasSeconds?: boolean,
  fullDate?: boolean,
  timeZone?: string,
) => string;

export const useDisplayDateRange = (): DisplayDateRangeFn => {
  const isLocalizedDateTimeFeatureFlagEnabled = useLocalizedDateTimeFeatureFlag();

  return (start, end, showTime, twentyFourHour, hasSeconds, fullDate, timeZone) =>
    isLocalizedDateTimeFeatureFlagEnabled && start instanceof Date && end instanceof Date
      ? (localizeDateRange(start, end).range ?? '')
      : displayDateRange(start, end, showTime, twentyFourHour, hasSeconds, fullDate, timeZone);
};
